import { useState, useMemo } from 'react';
import { Check, ChevronsUpDown, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Skeleton } from '@/components/ui/skeleton';
import { useFilterOptions } from '../../hooks';
import { summarizeSelection } from '../../lib/selection-summary';
import { selectAllLabel } from '../../lib/select-all-label';
import { withArchivedHidden } from '@/lib/archived-projects';
import { ShowArchivedToggle } from '@/components/show-archived-toggle';
import type { FilterFieldConfig } from '../../types';
import { cn } from '@/lib/utils';

// ============================================================================
// Types
// ============================================================================

interface MultiSelectFilterProps {
  /** Field configuration */
  config: FilterFieldConfig<string[]>;
  /** Current selected values */
  value: string[];
  /** Change handler */
  onChange: (value: string[]) => void;
  /** Whether the field is disabled */
  disabled?: boolean;
  /** Additional className for the container */
  className?: string;
  /** Hide the label */
  hideLabel?: boolean;
  /** Maximum badges to show before collapsing */
  maxBadges?: number;
}

// ============================================================================
// Component
// ============================================================================

/**
 * Multi-select filter field with checkboxes, search, and badge display.
 *
 * @example
 * ```tsx
 * <MultiSelectFilter
 *   config={{
 *     key: 'departmentIds',
 *     type: 'multi-select',
 *     label: 'Departments',
 *     placeholder: 'All departments',
 *     defaultValue: [],
 *     options: 'departments',
 *   }}
 *   value={filters.departmentIds}
 *   onChange={(value) => setFilter('departmentIds', value)}
 * />
 * ```
 */
export function MultiSelectFilter({
  config,
  value = [],
  onChange,
  disabled = false,
  className,
  hideLabel = false,
  maxBadges = 3,
}: MultiSelectFilterProps) {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const { options, isLoading } = useFilterOptions(config.options);

  // A source that flags inactive options (archived and on-hold projects) lists
  // them only behind the "Show archived" toggle — or while selected, so a saved
  // report never hides what it is filtered by. Every other source flags none,
  // and nothing here changes for it. The trigger's summary still reads the
  // full `options`, to name any selection. [HW-951]
  const hasInactive = useMemo(() => options.some((opt) => opt.inactive), [options]);
  const listedOptions = useMemo(
    () =>
      withArchivedHidden(options, (opt) => opt.inactive === true, (opt) => opt.value, {
        showArchived: showInactive,
        keep: value,
      }),
    [options, showInactive, value],
  );

  // Determine placeholder text
  const placeholder = config.placeholder ?? `Select ${config.label.toLowerCase()}...`;

  // What the closed trigger says. Derived from the VALUE rather than from the
  // options it managed to resolve — see `summarizeSelection`.
  const summary = useMemo(
    () => summarizeSelection(options, value, maxBadges),
    [options, value, maxBadges],
  );

  // Filter options by search query
  const filteredOptions = useMemo(() => {
    if (!searchQuery) return listedOptions;
    const query = searchQuery.toLowerCase();
    return listedOptions.filter(
      (opt) =>
        opt.label.toLowerCase().includes(query) ||
        opt.description?.toLowerCase().includes(query)
    );
  }, [listedOptions, searchQuery]);

  // Check if all filtered options are selected
  const allFilteredSelected = useMemo(() => {
    if (filteredOptions.length === 0) return false;
    return filteredOptions.every((opt) => value.includes(opt.value));
  }, [filteredOptions, value]);

  // Handle toggle single option
  const handleToggle = (optionValue: string) => {
    if (value.includes(optionValue)) {
      onChange(value.filter((v) => v !== optionValue));
    } else {
      onChange([...value, optionValue]);
    }
  };

  // Handle select all filtered
  const handleSelectAllFiltered = () => {
    if (allFilteredSelected) {
      // Deselect all filtered
      const filteredValues = new Set(filteredOptions.map((o) => o.value));
      onChange(value.filter((v) => !filteredValues.has(v)));
    } else {
      // Select all filtered
      const newValues = new Set([...value, ...filteredOptions.map((o) => o.value)]);
      onChange(Array.from(newValues));
    }
  };

  // Handle clear all
  const handleClearAll = () => {
    onChange([]);
  };

  // Handle remove single badge
  const handleRemoveBadge = (optionValue: string, e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(value.filter((v) => v !== optionValue));
  };

  // Loading state
  if (isLoading) {
    return (
      <div className={cn('space-y-2', className)}>
        {!hideLabel && (
          <label className="text-sm font-medium text-foreground">
            {config.label}
          </label>
        )}
        <Skeleton className="h-10 w-full" />
      </div>
    );
  }

  return (
    <div className={cn('space-y-2', className)}>
      {!hideLabel && (
        <label className="text-sm font-medium text-foreground">
          {config.label}
        </label>
      )}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="w-full justify-between font-normal min-h-10 h-auto py-2"
            disabled={disabled}
          >
            <div className="flex flex-wrap gap-1 flex-1 text-left">
              {summary.kind === 'empty' ? (
                <span className="text-muted-foreground">{placeholder}</span>
              ) : summary.kind === 'count' ? (
                <Badge variant="secondary">{summary.count} selected</Badge>
              ) : (
                <>
                  {summary.options.map((option) => (
                    <Badge
                      key={option.value}
                      variant="secondary"
                      className="mr-1 mb-0.5"
                    >
                      {option.label}
                      <button
                        type="button"
                        className="ml-1 rounded-full outline-none ring-offset-background focus:ring-2 focus:ring-ring focus:ring-offset-2"
                        onClick={(e) => handleRemoveBadge(option.value, e)}
                      >
                        <X className="h-3 w-3 text-muted-foreground hover:text-foreground" />
                      </button>
                    </Badge>
                  ))}
                  {/* A selected value this list cannot name — a retired
                      employment type, a deactivated position, options still in
                      flight. It stays visible: the alternative is a control
                      claiming to filter nothing while the report is narrowed. */}
                  {summary.unnamed > 0 && (
                    <Badge variant="outline" className="mr-1 mb-0.5 text-muted-foreground">
                      {summary.unnamed} not listed
                    </Badge>
                  )}
                </>
              )}
            </div>
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
          <Command shouldFilter={false}>
            <CommandInput
              placeholder={`Search ${config.label.toLowerCase()}...`}
              value={searchQuery}
              onValueChange={setSearchQuery}
            />
            <CommandList>
              {/* Select All / Clear All actions */}
              <CommandGroup>
                <CommandItem
                  onSelect={handleSelectAllFiltered}
                  className="cursor-pointer"
                >
                  <Checkbox
                    checked={allFilteredSelected}
                    className="mr-2"
                  />
                  <span className="flex-1">
                    {selectAllLabel({
                      rows: searchQuery ? filteredOptions : listedOptions,
                      searching: !!searchQuery,
                      inactiveHidden: hasInactive && !showInactive,
                    })}
                  </span>
                </CommandItem>
                {value.length > 0 && (
                  <CommandItem
                    onSelect={handleClearAll}
                    className="cursor-pointer text-muted-foreground"
                  >
                    <X className="mr-2 h-4 w-4" />
                    Clear selection ({value.length})
                  </CommandItem>
                )}
              </CommandGroup>

              <CommandSeparator />

              {/* Options list */}
              {filteredOptions.length === 0 ? (
                <CommandEmpty>
                  {searchQuery ? 'No results found.' : 'No options available.'}
                </CommandEmpty>
              ) : (
                <CommandGroup>
                  {filteredOptions.map((option) => {
                    const isSelected = value.includes(option.value);
                    return (
                      <CommandItem
                        key={option.value}
                        value={option.value}
                        onSelect={() => handleToggle(option.value)}
                        disabled={option.disabled}
                        className="cursor-pointer"
                      >
                        <Checkbox
                          checked={isSelected}
                          className="mr-2"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between">
                            <span className="truncate">{option.label}</span>
                            {option.count !== undefined && (
                              <span className="text-xs text-muted-foreground ml-2">
                                ({option.count})
                              </span>
                            )}
                          </div>
                          {option.description && (
                            <div className="text-xs text-muted-foreground truncate">
                              {option.description}
                            </div>
                          )}
                        </div>
                        {isSelected && (
                          <Check className="ml-2 h-4 w-4 text-primary flex-shrink-0" />
                        )}
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
              )}
            </CommandList>
            {hasInactive && (
              <div className="border-t border-border px-3 py-2">
                <ShowArchivedToggle
                  id={`${config.key}-show-archived`}
                  checked={showInactive}
                  onCheckedChange={setShowInactive}
                />
              </div>
            )}
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}
